import { createContext, useContext, useState } from "react";

export const translations = {
  en: {
    nav: { about: "The Group", sectors: "Sectors", portfolio: "Portfolio", history: "Legacy", vision: "Vision", contact: "Get in Touch" },
    hero: {
      title1: "A Legacy",
      title2: "of Excellence",
      subtitle: "For over three decades, Chespi Group has shaped Romania's business landscape across seven sectors — built on family, integrity, and an uncompromising pursuit of quality.",
      meta: ["Est. 1995", "Constanta, Romania", "7 sectors", "120+ professionals"],
      scroll: "Scroll",
    },
    about: {
      overline: "Who We Are",
      title: "Seven industries. Three sub-groups. One family.",
      p1: "Chespi Group is a family-owned group of companies based in Constanta, Romania, operating across seven business sectors: Residential & Commercial Real Estate, Tourism & Hospitality, Finance, Energy, Retail and Farming.",
      p2: "Established more than 30 years ago, we bring together an impressive portfolio of private businesses and sub-groups — Meliora, Taurus and Corsa Exchange — alongside more than 120 well-qualified professionals. Whatever the field, customer satisfaction always comes first.",
      founder: "Founded in 1995 by CEO Chespi Tagedin, whose dedication, market understanding and perfectionism have left an undeniable mark on every member of the team.",
    },
    pillars: {
      overline: "What Defines Us",
      title: "Four pillars. One philosophy.",
      items: [
        { t: "Family", d: "We treat every client and team member as family — the most valuable asset in life and in business." },
        { t: "Integrity", d: "Honesty and strong principles are carried throughout every member of the group." },
        { t: "Perseverance", d: "We work assiduously toward every goal, confident that dedication delivers results." },
        { t: "Courage", d: "Success is impossible without courage. Calculated risks have allowed us to grow every single year." },
      ],
    },
    sectors: {
      overline: "Our Industries",
      title: "Seven sectors, one standard of excellence",
      subtitle: "A diversified portfolio spanning the most demanding markets in Romania.",
      soon: "Coming Soon",
      items: [
        { k: "residential", t: "Residential Real Estate", d: "Landmark developments and luxury residences in Constanta's most exclusive locations — by Meliora Group." },
        { k: "commercial", t: "Commercial Real Estate", d: "Premium office and retail buildings, home to internationally renowned brands — by Taurus Group." },
        { k: "tourism", t: "Tourism & Hospitality", d: "Four-star resort, fine dining and Romania's most exclusive event center, on the shore of the Black Sea." },
        { k: "finance", t: "Finance", d: "Corsa Exchange — market leader in Constanta with 12 locations and over €350M exchanged." },
        { k: "energy", t: "Energy", d: "A five-hectare photovoltaic park and an upcoming EV charging network — clean energy for the future." },
        { k: "retail", t: "Retail", d: "Exclusive alcoholic beverages and cigars, curated within a chic boutique setting." },
        { k: "farm", t: "Farming", d: "A new livestock farm is taking shape — laying hens and farm-fresh eggs, soon to join the Chespi Group family.", soon: true },
      ],
    },
    portfolio: {
      overline: "The Portfolio",
      title: "Three sub-groups leading their markets",
      subLabel: "The 3 sub-groups",
      subgroups: [
        { name: "Meliora Group", sector: "Residential Real Estate", d: "Bold, large-scale residential developments defined by modern architecture, premium finishes and absolute privacy.", items: ["Meliora Tabacarie Lake", "Meliora Poporului", "Meliora Resort Venus (Coming Soon)", "Meliora Tower Mamaia (Coming Soon)"] },
        { name: "Taurus Group", sector: "Commercial Real Estate", d: "Award-winning commercial buildings in Constanta's most important locations, home to global brands.", items: ["Diplomatic Business Center", "220 Mamaia Business Center", "46 Ferdinand Business Center", "Dacia Business Center"] },
        { name: "Corsa Exchange Group", sector: "Finance", d: "The currency exchange market leader in Constanta — 12 locations, 14 currencies", items: ["Headquarters — Str. Poporului 23", "City Park Mall — Carrefour entrance", "City Park Mall - lifts area", "Vivo! Constanta — Bd. Aurel Vlaicu 220", "Vivo! Constanta — Bd. Aurel Vlaicu 220 (2)", "Eden — Str. Soveja 77", "Kaufland ICIL — Bd. 1 Decembrie 21", "Kaufland Coiciu — Str. Ion Roata 3", "Navodari — Str. Constantei 15", "Kaufland Navodari — Str. Constantei 26", "Kaufland Mangalia — Sos. Gral. Al. Vartejanu 1A", "Kaufland Mangalia Lavrion — Str. Lavrion 30A"] },
      ],
      moreTitle: "Beyond",
      moreSubtitle: "Four further divisions complete the group across tourism, energy, retail and livestock.",
      divisions: [
        { name: "Chespi Hospitality", sector: "Tourism & Hospitality", items: ["Enigma Resort Mamaia", "Diplomatic Event Center", "Oro Restaurant Mamaia", "Self-Service Restaurant", "Cinque Sensi Pizzeria", "Kova Events (Coming Soon)"] },
        { name: "Chespi Energy", sector: "Energy", items: ["Photovoltaic Park Costinesti"] },
        { name: "Chespi Retail", sector: "Retail", items: ["The Liquor Store & More"] },
        { name: "Chespi Farming", sector: "Livestock", items: ["Laying hens & fresh eggs (Coming Soon)"] },
      ],
    },
    timeline: {
      overline: "Our Legacy",
      title: "Three decades of building",
      events: [
        { y: "1995", name: "Corsa Exchange", t: "Founded with its first location on Str. Poporului 23. Today the market leader in Constanta, with 12 locations across the city." },
        { y: "1998", name: "220 Mamaia Business Center", t: "Our first step into the world of real estate. Today home to partners Medstar 2000 and Toldimed medical and dental clinics." },
        { y: "2000", name: "Enigma Resort Mamaia", t: "A bungalow-type resort, perfect for a family getaway, in Mamaia's prime location." },
        { y: "2000", name: "Self-Service Restaurant Mamaia", t: "Home-cooked style food, served right on the beach." },
        { y: "2007", name: "Dacia Business Center", t: "An office and retail building in one of Constanta's most populated areas, home to partner Famous Fast Food." },
        { y: "2008", name: "Meliora Tabacarie Lake", t: "Our first residential project, in the most exclusive location of Constanta, with breathtaking views of the Tabacarie Lake and Park." },
        { y: "2008", name: "46 Ferdinand Business Center", t: "Winner of the best architecture design award, in an impressive location. Today home to partner Laser Vision Med, a leading ophthalmology clinic." },
        { y: "2010", name: "Meliora Poporului", t: "Our second residential building, standing in the heart of Constanta, built to the highest standards of quality." },
        { y: "2013", name: "Enigma Resort — Renovated", t: "Fully renovated to a high-end, luxurious level with modern architecture — over 90% occupancy every summer since 2013." },
        { y: "2017", name: "Diplomatic Business Center", t: "A premium commercial building in one of Constanta's key locations, home to partners Kik, Jysk and Carrefour Express." },
        { y: "2019", name: "The Liquor Store & More", t: "Chespi Group's first step into the retail sector — exclusive alcoholic beverages and cigars, in a chic boutique." },
        { y: "2019", name: "Oro Restaurant Mamaia", t: "The most upscale fine dining restaurant in Mamaia, with astonishing views of the Black Sea and a laid-back atmosphere." },
        { y: "2019", name: "Self-Service Restaurant — Renovated", t: "Fully renovated to the highest standards, keeping its mission to serve home-cooked style food." },
        { y: "2019", name: "Diplomatic Event Center", t: "Known as the first nightclub in Mamaia under the name 'Enigma Club', since transformed into one of Romania's most exclusive event centers." },
        { y: "2026", name: "Cinque Sensi Pizzeria", t: "Our first step into the world of pizza — an authentic Italian pizzeria in the heart of Constanta, with handcrafted pizzas, slow-fermented dough and premium ingredients that bring the genuine taste of Italy to the Black Sea coast." },
        { y: "2026", name: "Photovoltaic Park Costinesti", t: "Our first step into the energy sector — a 5-hectare park aiming to produce 3MW of green energy per year." },
        { y: "2027", name: "Chespi Farming", t: "Chespi Group's first step into livestock farming — laying hens and farm-fresh eggs." },
        { y: "2028", name: "Meliora Tower Mamaia", t: "An upcoming residential development on the Mamaia seafront." },
        { y: "2029", name: "Meliora Resort Venus", t: "An upcoming residential landmark by Meliora Group." },
      ],
    },
    vision: {
      overline: "Vision & Mission",
      vtitle: "Our Vision",
      vtext: "To set a distinguished standard of service, becoming a role model for entrepreneurs and businesses on the Romanian market — growing with every opportunity while preserving an elevated level of quality.",
      mtitle: "Our Mission",
      mtext: "To achieve success on a European level through proximity to international standards. Our focus on quality and visionary projects is what sets Chespi Group apart and what will continue to drive our growth.",
    },
    partners: { title: "Trusted partners under our roof" },
    footer: {
      tagline: "A family legacy, building Romania's future.",
      cta: "Get in Touch",
      contactTitle: "Let's talk",
      contactText: "For partnerships, leasing or investment enquiries, our team is ready to assist you.",
      email: "office@chespigroup.com",
      phone: "0729 440 815",
      address: "Constanta, Romania",
      sectorsTitle: "Sectors",
      deptsTitle: "Contact by sector",
      mailSubject: "Inquiry — ",
      depts: [
        { label: "Residential Real Estate", email: "office@chespigroup.com" },
        { label: "Commercial Real Estate", email: "office@chespigroup.com" },
        { label: "Finance", email: "office@corsaexchange.ro" },
        { label: "Tourism & Hospitality", email: "diplomaticvanzari@gmail.com" },
        { label: "Energy", email: "office@chespigroup.com" },
        { label: "Retail", email: "diplomaticvanzari@gmail.com" },
        { label: "Farming", email: "office@chespigroup.com" },
      ],
      rights: "All rights reserved.",
      note: "Family · Integrity · Perseverance · Courage",
    },
    finance: {
      overline: "Finance",
      brand: "Corsa Exchange",
      aboutTitle: "About Corsa Exchange",
      aboutLead: "Three decades of currency exchange in Constanta.",
      p1: "Corsa International Change Office SRL was established in 1995 and today counts 12 working points across Constanta, Navodari and Mangalia.",
      p2: "Our list comprises 14 currencies, and rates are negotiable depending on the amounts exchanged. We offer our services with promptness and professionalism.",
      cta: "Visit corsaexchange.ro",
      stats: [
        { value: "12", label: "Locations" },
        { value: "14", label: "Currencies" },
        { value: "1995", label: "Since" },
        { value: "€350M+", label: "Exchanged" },
      ],
      locationsTitle: "Our locations",
      locationsSubtitle: "12 working points across Constanta, Navodari and Mangalia.",
      contactTitle: "Contact",
      hqLabel: "Headquarters",
      back: "Back to home",
      offices: [
        { img: "pozaSediuCentral.jpeg", name: "Headquarters", addr: "Poporului St. No. 23, Constanta" },
        { img: "city1.jpeg", name: "City Park Mall — Branch 1", addr: "City Park Mall, Carrefour entrance, level -1, Constanta" },
        { img: "city2.jpeg", name: "City Park Mall — Branch 2", addr: "City Park Mall, level 0, lifts area, Constanta" },
        { img: "vivo2.jpeg", name: "Vivo! Constanta — Branch 1", addr: "Aurel Vlaicu Blvd. 220, Constanta" },
        { img: "maritimo1.jpeg", name: "Vivo! Constanta — Branch 2", addr: "Aurel Vlaicu Blvd. 220, Constanta" },
        { img: "eden.jpg", name: "Eden", addr: "Soveja St. No. 77, Constanta" },
        { img: "kauflandIcil.jpeg", name: "Kaufland Constanta — ICIL", addr: "1 Decembrie Blvd. No. 21, Constanta" },
        { img: "kauflandCoiciu.jpeg", name: "Kaufland Constanta — Coiciu", addr: "Ion Roata St. No. 3, Constanta" },
        { img: "navodari.jpeg", name: "Navodari", addr: "Navodari, Constantei St. No. 15" },
        { img: "kauflandNavodari.jpeg", name: "Kaufland Navodari", addr: "Constantei St. No. 26, Navodari" },
        { img: "KauflandVatajeanu.jpeg", name: "Kaufland Mangalia", addr: "Gral. Alexandru Vartejanu Rd. No. 1A, Mangalia" },
        { img: "KauflandSaturn.jpeg", name: "Kaufland Mangalia — Lavrion", addr: "Lavrion St. No. 30A, Mangalia" },
      ],
    },
    retail: {
      overline: "Retail",
      brand: "The Liquor Store & More",
      aboutLead: "Over 900 products in permanent stock, in the north of Mamaia resort.",
      p1: "We keep a wide range of drinks in stock — from Whiskey, Vodka, Tequila, Gin, Cognac and Rum to collection wines, fine beer and Champagne. You will find over 900 products in permanent stock.",
      categoriesTitle: "What you'll find",
      categories: [
        { img: "whisky-mobil1.png", name: "Whiskey", desc: "When you truly love a fine spirit, it matters to know as much as you can about it. No other drink attracts more experts than whiskey.", brands: ["Jack Daniel's", "Johnnie Walker Black Label", "Chivas Regal"] },
        { img: "vodka-mobil1.png", name: "Vodka", desc: "A premium selection for the most demanding palates. Vodka takes on any taste you wish — which is why classic vodka cocktails are found everywhere.", brands: ["Smirnoff", "Absolut", "Finlandia"] },
        { img: "vinuri-mobil11.png", name: "Collection Wines", desc: "Today we have access to a wide range of wines from all over the world. At The Liquor Store & More we introduce you to the world of collection wines — remarkable bottles to delight your palate.", brands: [] },
      ],
      locationTitle: "Close to you in Mamaia",
      locationText: "The Liquor Store & More was designed to serve the northern area of Mamaia.",
      cta: "See more",
      contactTitle: "Contact",
      back: "Back to home",
    },
    energy: {
      overline: "Energy",
      brand: "Chespi Energy",
      aboutLead: "Sustainable, green energy for the future.",
      p1: "Clean, renewable power is more than a project for us — it is a promise to the generations that follow. Through Chespi Energy we turn the sun into green electricity, lowering emissions and helping build a more sustainable future for Constanta.",
      p2: "Chespi Group has always aimed to be an environmentally cautious company within the parameters of the sectors in which it operates. This commitment led to a new division — Chespi Energy — which oversees the production of sustainable, green energy.",
      projectTitle: "Photovoltaic Park Costinesti",
      projectText: "The first project for Chespi Energy is the development of a photovoltaic panels park in Costinesti. The park spans 5 hectares, aiming to produce 3MW per year, starting in 2026.",
      stats: [
        { value: "5 ha", label: "Park area" },
        { value: "3 MW", label: "Annual output" },
        { value: "2026", label: "Operational since" },
      ],
      back: "Back to home",
    },
    tourism: {
      overline: "Chespi Hospitality",
      heroBrand: "Diplomatic Event Center",
      heroTagline: "Where the Black Sea meets the art of celebration.",
      diploLead: "Mamaia's market leader in luxury events.",
      diploText:
        "Overlooking the beaches of Mamaia, Diplomatic hosts weddings, baptisms and corporate events across salons that divide to suit any occasion — with two pools and a generous terrace, steps from the sea.",
      diploBody: [
        "Completed in 2018, Diplomatic Event Center overlooks the beaches of Mamaia and the Black Sea — the market leader in Constanta, with the highest level of luxury in both service and setting.",
        "Classical interior architecture meets a modern, minimalist exterior to create a remarkable ambience for any occasion. With a capacity of 1,000+ guests, the venue divides into two, three or four salons, opening onto a generous terrace with two swimming pools, steps from the sea.",
        "From weddings, baptisms and pool parties to corporate evenings and conferences — whatever you are planning, our team is there from the smallest detail to the moment itself.",
      ],
      diploCta: "complexdiplomatic.ro",
      lumiereLabel: "Salon Lumière",
      salonsLabel: "Salon Sunrise & Sunset",
      stats: [
        { value: "1000+", label: "Guest capacity" },
        { value: "4", label: "Modular salons" },
        { value: "2", label: "Swimming pools" },
        { value: "2.533 m²", label: "Built surface" },
      ],
      oroBrand: "Oro Restaurant",
      oroLead: "Modern European cuisine, directly on the beach.",
      oroText:
        "A bijou restaurant within the Diplomatic complex — marble, gold accents and just 60 seats, so exclusivity is found on every plate.",
      oroBody: [
        "Opened in 2019 and set within the Diplomatic Event Center complex — in the heart of Mamaia, directly on the beach — Oro is one of the resort's most upscale tables, serving modern European cuisine.",
        "Live-cooking evenings bring the kitchen out to the terrace, where the chefs plate before you as the sun sets over the Black Sea.",
      ],
      enigmaBrand: "Enigma Resort",
      enigmaLead: "A four-star escape, meters from the silky sand of Mamaia.",
      enigmaText:
        "Six bungalows wrapped in 2000 m² of greenery, a private beach and breakfast at Oro.",
      enigmaCta: "Book on Booking.com",
      cinqueBrand: "Cinque Sensi",
      cinqueLead: "Authentic Italian pizza, in the heart of Constanta.",
      cinqueBody: [
        "At Cinque Sensi Pizzeria we bring the authentic taste of Italy to Constanta — handcrafted pizzas made with premium ingredients, slow-fermented dough and a passion for quality. Every bite is a celebration of tradition, flavor and excellence.",
        "Our commitment to quality, freshness and customer satisfaction has made us a favorite destination for pizza lovers seeking genuine Italian excellence.",
      ],
      back: "Back to home",
    },
    residential: {
      overline: "Meliora Group",
      heroBrand: "Meliora",
      heroTagline: "Luxury living on the shores of the Black Sea.",
      heroLine2: "Creating inspiring places for generations to come.",
      lead: "Landmark residential developments in Constanta's most coveted locations.",
      body: [
        "Meliora Group develops landmark residential projects — bold, large-scale buildings defined by striking modern architecture, premium finishes and uncompromising quality.",
        "From the shores of Lake Tabacarie to the Mamaia seafront, we build on an ambitious scale — landmark addresses designed to offer their residents a distinct level of exclusivity.",
      ],
      towerBrand: "Meliora Tower Mamaia",
      towerLead: "An exclusive beachfront landmark on the Mamaia seafront.",
      towerBody: [
        "Meliora Tower Mamaia is an exclusive beachfront development designed to offer a refined residential experience in one of the area's most desirable locations. Rising 16 floors on a 4,000 m² prime seafront plot, the project combines contemporary architecture, premium amenities, and panoramic coastal views.",
        "Thoughtfully designed to balance sophistication and comfort, Meliora presents spacious residences, beautifully landscaped outdoor areas, and a lifestyle defined by privacy, convenience, and understated luxury.",
      ],
      amenitiesTitle: "Amenities",
      amenities: ["Private Parking", "Outdoor Pool", "Spa", "Gym", "24/7 Concierge", "Private Beach"],
      tabacarieiBrand: "Meliora Tabacarie Lake",
      tabacarieiLead: "Our first residential landmark, by the Tabacarie Lake and Park.",
      tabacarieiBody: [
        "Meliora Tabacarie Lake was the first step for Meliora Group in the residential real estate sector. Completed in 2008, it sits at the southern entrance to Mamaia — a stylish, boutique-type development that enjoys unrestricted panoramic views over the park and the lake, in a quiet and exclusive area.",
        "The location offers quick access to both the city centre and Mamaia. With a G+5 building regime and a total surface of 1,635 m², the development accommodates 10 apartments.",
      ],
      tabacarieiStats: [
        { value: "2008", label: "Completed" },
        { value: "G+5", label: "Building regime" },
        { value: "1.635 m²", label: "Total surface" },
        { value: "10", label: "Apartments" },
      ],
      poporuluiBrand: "Meliora Poporului",
      poporuluiLead: "Built to the highest standards, in the heart of Constanta.",
      poporuluiBody: [
        "Meliora Poporului was completed in 2010. Situated in the heart of Constanta, it was built to the highest standards, offering spacious living spaces and private underground parking to all residents.",
        "The property stands out through its modern architecture, allowing the apartments to provide their residents with a distinct level of exclusivity.",
      ],
      stats: [
        { value: "2010", label: "Completed" },
        { value: "G+5", label: "Building regime" },
        { value: "2.117 m²", label: "Total surface" },
        { value: "10", label: "Apartments" },
        { value: "2", label: "Commercial spaces" },
      ],
      back: "Back to home",
    },
    taurus: {
      overline: "Commercial Real Estate",
      brand: "Taurus Group",
      kicker: "A Chespi Group sub-group",
      heroLead:
        "Award-winning commercial landmarks in Constanta's most important locations — home to globally recognised brands.",
      established: "Est. 1998 · Constanta",
      scroll: "Scroll to explore",
      portfolioOverline: "The Portfolio",
      portfolioTitle: "Four landmarks",
      portfolioLead:
        "Four office and retail buildings that shape Taurus Group's commercial footprint across Constanta.",
      buildings: [
        { k: "diplomatic", index: "01", name: "Diplomatic Business Center", year: "2017", location: "Abator, Constanta", regime: "G+1", area: "3.332 m²", tag: "The group's largest commercial property — home to Jysk, Carrefour and Kik.", href: "#diplomatic" },
        { k: "ferdinand", index: "02", name: "46 Ferdinand Business Center", year: "2008", location: "Ferdinand St., Constanta", regime: "UG+G+3", area: "1.387 m²", tag: "Best Architecture of the Year — home to Laser Vision Med.", href: "#ferdinand" },
        { k: "mamaia", index: "03", name: "220 Mamaia Business Center", year: "1998", location: "Mamaia Blvd., Constanta", regime: "G+1", area: "700 m²", tag: "The group's first commercial building, home to Medstar 2000 and Toldimed.", href: "#mamaia" },
        { k: "dacia", index: "04", name: "Dacia Business Center", year: "2007", location: "Lapusneanu × Tomis, Constanta", regime: "G+1", area: "160 m²", tag: "Acquired and fully renovated in 2007, on one of the city's busiest intersections.", href: "#dacia" },
      ],
      diplomatic: {
        tag: "Flagship",
        name: "Diplomatic Business Center",
        lead: "Taurus Group's largest commercial property today.",
        body: [
          "Originally an event center, Diplomatic Business Center was reborn as a modern commercial landmark in the Abator area of Constanta.",
          "It is now home to a number of internationally successful retail stores — Jysk, Carrefour and Kik. With a G+1 regime, 3,332 m² of rentable area and 25 parking spaces, the building's modern design beautifully complements the urban area in which it stands.",
          "The development was conceived as an upgrade for the entire neighbourhood, bringing the services and products of internationally renowned stores closer to the community.",
        ],
        specs: [
          { k: "Completed", v: "2017" },
          { k: "Regime", v: "G+1" },
          { k: "Rentable area", v: "3.332 m²" },
          { k: "Parking", v: "25 spaces" },
          { k: "Location", v: "Abator, Constanta" },
        ],
        tenantsLabel: "Home to",
        tenants: ["Jysk", "Carrefour", "Kik"],
        galleryOverline: "Gallery",
        galleryTitle: "Inside Diplomatic",
      },
      ferdinand: {
        tag: "Award winner",
        name: "46 Ferdinand Business Center",
        award: "Best Architecture of the Year — Constanta, 2008",
        lead: "One of Taurus Group's most important assets.",
        body: [
          "Built in 2008, 46 Ferdinand Business Center received the prize for the best architecture of the year in Constanta.",
          "It stands on Ferdinand Street — one of the city's most important commercial and financial arteries — distinguished by an international standard of quality, and built to adapt to either office or commercial use.",
          "Today it is home to Laser Vision Med, one of Romania's most successful ophthalmology clinics.",
        ],
        specs: [
          { k: "Completed", v: "2008" },
          { k: "Regime", v: "UG+G+3" },
          { k: "Built area", v: "1.387 m²" },
          { k: "Location", v: "Ferdinand St., Constanta" },
        ],
      },
      mamaia: {
        tag: "The first",
        name: "220 Mamaia Business Center",
        lead: "Taurus Group's first commercial real estate building.",
        body: [
          "Set on the most popular boulevard of Constanta, 220 Mamaia Business Center was built to the highest standards and designed to adapt to the needs of any tenant.",
          "Completed in 1998, it is where Taurus Group's commercial story began — and is today home to partners Medstar 2000 and Toldimed, a medical and a dental clinic.",
        ],
        specs: [
          { k: "Completed", v: "1998" },
          { k: "Regime", v: "G+1" },
          { k: "Built area", v: "700 m²" },
          { k: "Location", v: "Mamaia Blvd., Constanta" },
        ],
        tenantLabel: "Home to",
        tenants: ["Medstar 2000", "Toldimed"],
      },
      dacia: {
        tag: "City landmark",
        name: "Dacia Business Center",
        lead: "Acquired and fully renovated by Taurus Group in 2007.",
        body: [
          "Set on the intersection of Boulevards Alexandru Lapusneanu and Tomis, Dacia Business Center guarantees any tenant a constant flow of traffic in one of Constanta's most populated areas.",
          "Its large windows and modern architecture feel like a breath of fresh air for the area, with direct access from the boulevard and generous parking.",
        ],
        specs: [
          { k: "Acquired", v: "2007" },
          { k: "Regime", v: "G+1" },
          { k: "Built area", v: "160 m²" },
          { k: "Location", v: "Lapusneanu × Tomis, Constanta" },
        ],
        tenantLabel: "Home to",
        tenants: ["Famous Fast Food"],
      },
      contactOverline: "Commercial Real Estate",
      contactTitle: "Build with Taurus",
      contactText: "For leasing and commercial enquiries across the Taurus Group portfolio.",
      contactEmail: "commercial@chespigroup.ro",
      back: "Back to home",
    },
  },
  ro: {
    nav: { about: "Grupul", sectors: "Sectoare", portfolio: "Portofoliu", history: "Istoric", vision: "Viziune", contact: "Contactează-ne" },
    hero: {
      title1: "O moștenire",
      title2: "a excelenței",
      subtitle: "De peste trei decenii, Chespi Group modelează peisajul de afaceri al României în șapte sectoare — clădit pe familie, integritate și o căutare neîntreruptă a calității.",
      meta: ["Est. 1995", "Constanța, România", "7 sectoare", "120+ profesioniști"],
      scroll: "Derulează",
    },
    about: {
      overline: "Cine suntem",
      title: "Șapte industrii. Trei sub-grupuri. O familie.",
      p1: "Chespi Group este un grup de companii de familie cu sediul în Constanța, România, activ în șapte sectoare de afaceri: imobiliare rezidențiale și comerciale, turism și ospitalitate, finanțe, energie, retail și creșterea animalelor.",
      p2: "Înființat acum peste 30 de ani, reunim un portofoliu impresionant de afaceri private și sub-grupuri — Meliora, Taurus și Corsa Exchange — alături de peste 120 de profesioniști bine pregătiți. Indiferent de domeniu, satisfacția clientului este mereu pe primul loc.",
      founder: "Fondat în 1995 de CEO-ul Chespi Tagedin, a cărui dedicare, înțelegere a pieței și perfecționism au lăsat o amprentă de netăgăduit asupra fiecărui membru al echipei.",
    },
    pillars: {
      overline: "Ceea ce ne definește",
      title: "Patru piloni. O singură filozofie.",
      items: [
        { t: "Familie", d: "Tratăm fiecare client și membru al echipei ca pe o familie — cel mai valoros bun din viață și din afaceri." },
        { t: "Integritate", d: "Onestitatea și principiile solide sunt purtate de fiecare membru al grupului." },
        { t: "Perseverență", d: "Muncim asiduu pentru fiecare obiectiv, încrezători că dedicarea aduce rezultate." },
        { t: "Curaj", d: "Succesul este imposibil fără curaj. Riscurile calculate ne-au permis să creștem în fiecare an." },
      ],
    },
    sectors: {
      overline: "Industriile noastre",
      title: "Șapte sectoare, un singur standard al excelenței",
      subtitle: "Un portofoliu diversificat care acoperă cele mai exigente piețe din România.",
      soon: "În curând",
      items: [
        { k: "residential", t: "Imobiliare rezidențiale", d: "Dezvoltări de referință și reședințe de lux în cele mai exclusiviste locații din Constanța — by Meliora Group." },
        { k: "commercial", t: "Imobiliare comerciale", d: "Clădiri premium de birouri și retail, gazdă pentru branduri internaționale — by Taurus Group." },
        { k: "tourism", t: "Turism și ospitalitate", d: "Resort de patru stele, fine dining și cel mai exclusivist centru de evenimente din România, pe malul Mării Negre." },
        { k: "finance", t: "Finanțe", d: "Corsa Exchange — lider de piață în Constanța, cu 12 locații și peste 350M € schimbați." },
        { k: "energy", t: "Energie", d: "Un parc fotovoltaic de cinci hectare și o viitoare rețea de încărcare EV — energie verde pentru viitor." },
        { k: "retail", t: "Retail", d: "Băuturi alcoolice exclusiviste și trabucuri, într-un decor boutique elegant." },
        { k: "farm", t: "Fermă", d: "O nouă fermă de animale prinde contur — găini ouătoare și ouă proaspete, în curând parte din familia Chespi Group.", soon: true },
      ],
    },
    portfolio: {
      overline: "Portofoliul", 
      title: "Trei sub-grupuri care conduc piețele lor",
      subLabel: "Cele 3 sub-grupuri",
      subgroups: [
        { name: "Meliora Group", sector: "Imobiliare rezidențiale", d: "Dezvoltări rezidențiale ambițioase, la scară mare, definite de arhitectură modernă, finisaje de lux și intimitate absolută.", items: ["Meliora Tabacarie Lake", "Meliora Poporului", "Meliora Resort Venus (În curând)", "Meliora Tower Mamaia (În curând)"] },
        { name: "Taurus Group", sector: "Imobiliare comerciale", d: "Clădiri comerciale premiate, în cele mai importante locații din Constanța, gazdă pentru branduri globale.", items: ["Diplomatic Business Center", "220 Mamaia Business Center", "46 Ferdinand Business Center", "Dacia Business Center"] },
        { name: "Corsa Exchange Group", sector: "Finanțe", d: "Liderul pieței de schimb valutar din Constanța — 12 locații, 14 valute, zero comision.", items: ["Sediul Central — Str. Poporului 23", "City Park Mall — intrare Carrefour, nivel -1", "City Park Mall — nivel 0, zona lifturi", "Vivo! Constanța — Bd. Aurel Vlaicu 220", "Vivo! Constanța — Bd. Aurel Vlaicu 220 (2)", "Eden — Str. Soveja 77", "Kaufland ICIL — Bd. 1 Decembrie 21", "Kaufland Coiciu — Str. Ion Roata 3", "Năvodari — Str. Constanței 15", "Kaufland Năvodari — Str. Constanței 26", "Kaufland Mangalia — Șos. Gral. Al. Vârtejanu 1A", "Kaufland Mangalia Lavrion — Str. Lavrion 30A"] },
      ],
      moreTitle: "Dincolo",
      moreSubtitle: "Încă patru divizii completează grupul în turism, energie, retail și creșterea animalelor.",
      divisions: [
        { name: "Chespi Hospitality", sector: "Turism și ospitalitate", items: ["Enigma Resort Mamaia", "Diplomatic Event Center", "Oro Restaurant Mamaia", "Restaurant Self-Service", "Pizzeria Cinque Sensi", "Kova Events (În curând)"] },
        { name: "Chespi Energy", sector: "Energie", items: ["Parcul Fotovoltaic Costinești"] },
        { name: "Chespi Retail", sector: "Retail", items: ["The Liquor Store & More"] },
        { name: "Chespi Farming", sector: "Creșterea animalelor", items: ["Găini ouătoare & ouă proaspete (În curând)"] },
      ],
    },
    timeline: {
      overline: "Moștenirea noastră",
      title: "Trei decenii de construcție",
      events: [
        { y: "1995", name: "Corsa Exchange", t: "Fondată, cu prima locație pe Str. Poporului 23. Astăzi lider de piață în Constanța, cu 12 locații în oraș." },
        { y: "1998", name: "220 Mamaia Business Center", t: "Primul nostru pas în lumea imobiliarelor. Astăzi găzduiește partenerii Medstar 2000 și Toldimed — clinici medicale și stomatologice." },
        { y: "2000", name: "Enigma Resort Mamaia", t: "Un resort de tip bungalou, perfect pentru o escapadă în familie, în locația de top din Mamaia." },
        { y: "2000", name: "Restaurant Self-Service Mamaia", t: "Mâncare gătită ca acasă, servită chiar pe plajă." },
        { y: "2007", name: "Dacia Business Center", t: "O clădire de birouri și retail într-una dintre cele mai populate zone ale Constanței, gazdă pentru partenerul Famous Fast Food." },
        { y: "2008", name: "Meliora Tabacarie Lake", t: "Primul nostru proiect rezidențial, în cea mai exclusivistă locație din Constanța, cu priveliști spectaculoase asupra lacului și parcului Tăbăcărie." },
        { y: "2008", name: "46 Ferdinand Business Center", t: "Câștigător al premiului pentru cel mai bun design arhitectural, într-o locație impresionantă. Astăzi gazdă pentru partenerul Laser Vision Med, clinică de oftalmologie de top." },
        { y: "2010", name: "Meliora Poporului", t: "A doua clădire rezidențială, în inima Constanței, construită la cele mai înalte standarde de calitate." },
        { y: "2013", name: "Enigma Resort — Renovat", t: "Renovat complet la un nivel luxos, high-end, cu arhitectură modernă — peste 90% grad de ocupare în fiecare vară din 2013." },
        { y: "2017", name: "Diplomatic Business Center", t: "O clădire comercială premium într-una dintre locațiile cheie ale Constanței, gazdă pentru partenerii Kik, Jysk și Carrefour Express." },
        { y: "2019", name: "The Liquor Store & More", t: "Primul pas al Chespi Group în sectorul de retail — băuturi alcoolice exclusiviste și trabucuri, într-un magazin elegant." },
        { y: "2019", name: "Oro Restaurant Mamaia", t: "Cel mai rafinat restaurant fine dining din Mamaia, cu priveliști uimitoare asupra Mării Negre și o atmosferă relaxată." },
        { y: "2019", name: "Restaurant Self-Service — Renovat", t: "Renovat complet la cele mai înalte standarde, păstrându-și misiunea de a servi mâncare gătită ca acasă." },
        { y: "2019", name: "Diplomatic Event Center", t: "Cunoscut ca primul club din Mamaia sub numele 'Enigma Club', transformat de atunci într-unul dintre cele mai exclusiviste centre de evenimente din România." },
        { y: "2026", name: "Pizzeria Cinque Sensi", t: "Primul nostru pas în lumea pizzei — o pizzerie italiană autentică în inima Constanței, cu pizza preparată manual, aluat cu fermentare lentă și ingrediente premium, care aduc gustul autentic al Italiei pe malul Mării Negre." },
        { y: "2026", name: "Parcul Fotovoltaic Costinești", t: "Primul nostru pas în sectorul energetic — un parc de 5 hectare ce vizează producerea a 3MW de energie verde pe an." },
        { y: "2027", name: "Chespi Farming", t: "Primul pas al Chespi Group în creșterea animalelor — găini ouătoare și ouă proaspete." },
        { y: "2028", name: "Meliora Tower Mamaia", t: "O viitoare dezvoltare rezidențială pe faleza din Mamaia." },
        { y: "2029", name: "Meliora Resort Venus", t: "Un viitor reper rezidențial, by Meliora Group." },
      ],
    },
    vision: {
      overline: "Viziune și Misiune",
      vtitle: "Viziunea noastră",
      vtext: "Să stabilim un standard distins al serviciilor, devenind un model pentru antreprenorii și afacerile de pe piața românească — crescând cu fiecare oportunitate, păstrând un nivel ridicat de calitate.",
      mtitle: "Misiunea noastră",
      mtext: "Să atingem succesul la nivel european prin apropierea de standardele internaționale. Concentrarea pe calitate și pe proiecte vizionare este ceea ce diferențiază Chespi Group și ceea ce ne va susține creșterea.",
    },
    partners: { title: "Parteneri de încredere sub acoperișul nostru" },
    footer: {
      tagline: "O moștenire de familie, construind viitorul României.",
      cta: "Contactează-ne",
      contactTitle: "Hai să vorbim",
      contactText: "Pentru parteneriate, închirieri sau investiții, echipa noastră este pregătită să te ajute.",
      email: "office@chespigroup.com",
      phone: "0729 440 815",
      address: "Constanța, România",
      sectorsTitle: "Sectoare",
      deptsTitle: "Contact pe sectoare",
      mailSubject: "Solicitare — ",
      depts: [
        { label: "Imobiliare rezidențiale", email: "office@chespigroup.com" },
        { label: "Imobiliare comerciale", email: "office@chespigroup.com" },
        { label: "Finanțe", email: "office@corsaexchange.ro" },
        { label: "Turism și ospitalitate", email: "diplomaticvanzari@gmail.com" },
        { label: "Energie", email: "office@chespigroup.com" },
        { label: "Retail", email: "diplomaticvanzari@gmail.com" },
        { label: "Fermă", email: "office@chespigroup.com" },
      ],
      rights: "Toate drepturile rezervate.",
      note: "Familie · Integritate · Perseverență · Curaj",
    },
    finance: {
      overline: "Finanțe",
      brand: "Corsa Exchange",
      aboutTitle: "Despre Corsa Exchange",
      aboutLead: "Trei decenii de schimb valutar în Constanța.",
      p1: "Corsa International Change Office SRL a fost înființată în 1995 și numără astăzi 12 puncte de lucru în Constanța, Năvodari și Mangalia.",
      p2: "Lista noastră cuprinde 14 valute, iar cursurile sunt negociabile în funcție de sumele tranzacționate. Vă oferim serviciile noastre cu promptitudine și profesionalism.",
      cta: "Vizitează corsaexchange.ro",
      stats: [
        { value: "12", label: "Locații" },
        { value: "14", label: "Valute" },
        { value: "1995", label: "Din anul" },
        { value: "€350M+", label: "Schimbați" },
      ],
      locationsTitle: "Locațiile noastre",
      locationsSubtitle: "12 puncte de lucru în Constanța, Năvodari și Mangalia.",
      contactTitle: "Contact",
      hqLabel: "Sediul Central",
      back: "Înapoi la pagina principală",
      offices: [
        { img: "pozaSediuCentral.jpeg", name: "Sediul Central", addr: "Str. Poporului Nr. 23, Constanța" },
        { img: "city1.jpeg", name: "City Park Mall — Filiala 1", addr: "City Park Mall, intrare Carrefour, nivel -1, Constanța" },
        { img: "city2.jpeg", name: "City Park Mall — Filiala 2", addr: "City Park Mall, nivel 0, zona lifturi, Constanța" },
        { img: "vivo2.jpeg", name: "Vivo! Constanța — Filiala 1", addr: "Bulevardul Aurel Vlaicu 220, Constanța" },
        { img: "maritimo1.jpeg", name: "Vivo! Constanța — Filiala 2", addr: "Bulevardul Aurel Vlaicu 220, Constanța" },
        { img: "eden.jpg", name: "Eden", addr: "Strada Soveja nr. 77, Constanța" },
        { img: "kauflandIcil.jpeg", name: "Kaufland Constanța — ICIL", addr: "Bulevardul 1 Decembrie nr. 21, Constanța" },
        { img: "kauflandCoiciu.jpeg", name: "Kaufland Constanța — Coiciu", addr: "Strada Ion Roata nr. 3, Constanța" },
        { img: "navodari.jpeg", name: "Năvodari", addr: "Orașul Năvodari, Strada Constanței nr. 15" },
        { img: "kauflandNavodari.jpeg", name: "Kaufland Năvodari", addr: "Strada Constanței nr. 26, Năvodari" },
        { img: "KauflandVatajeanu.jpeg", name: "Kaufland Mangalia", addr: "Șoseaua Gral. Alexandru Vârtejanu nr. 1A, Mangalia" },
        { img: "KauflandSaturn.jpeg", name: "Kaufland Mangalia — Lavrion", addr: "Strada Lavrion nr. 30A, Mangalia" },
      ],
    },
    retail: {
      overline: "Retail",
      brand: "The Liquor Store & More",
      aboutLead: "Peste 900 de produse în stoc permanent, în nordul stațiunii Mamaia.",
      p1: "Avem în stoc o gamă largă de băuturi, de la Whiskey, Vodka, Tequila, Gin, Cognac și Rom la Vinuri de Colecție, bere deosebită și Champagne. Găsiți peste 900 de produse în stoc, în permanență.",
      categoriesTitle: "Ce vei găsi",
      categories: [
        { img: "whisky-mobil1.png", name: "Whiskey", desc: "Când iubești cu adevărat o băutură tare, este important să știi cât mai multe despre ea. Nicio altă băutură nu atrage mai mulți experți ca whiskey-ul.", brands: ["Jack Daniel's", "Johnnie Walker Black Label", "Chivas Regal"] },
        { img: "vodka-mobil1.png", name: "Vodka", desc: "Îți oferim o selecție premium pentru cele mai pretențioase papile. Vodka va avea orice gust dorești — de aceea cocktailurile clasice cu vodcă se găsesc peste tot.", brands: ["Smirnoff", "Absolut", "Finlandia"] },
        { img: "vinuri-mobil11.png", name: "Vinuri de Colecție", desc: "Astăzi avem acces la o gamă variată de vinuri din toată lumea. La The Liquor Store & More îți prezentăm lumea vinurilor de colecție — băuturi deosebite ce îți vor încânta bolta palatină.", brands: [] },
      ],
      locationTitle: "Aproape de tine în Mamaia",
      locationText: "Magazinul The Liquor Store & More a fost gândit să deservească zona de nord a stațiunii Mamaia.",
      cta: "Vezi mai multe",
      contactTitle: "Contact",
      back: "Înapoi la pagina principală",
    },
    energy: {
      overline: "Energie",
      brand: "Chespi Energy",
      aboutLead: "Energie verde, sustenabilă, pentru viitor.",
      p1: "Energia curată, regenerabilă, este pentru noi mai mult decât un proiect — este o promisiune făcută generațiilor care urmează. Prin Chespi Energy transformăm soarele în electricitate verde, reducând emisiile și construind un viitor mai sustenabil pentru Constanța.",
      p2: "Chespi Group a urmărit mereu să fie o companie responsabilă față de mediu, în limitele sectoarelor în care activează. Acest angajament a dus la o nouă divizie — Chespi Energy — care se ocupă de producția de energie verde, sustenabilă.",
      projectTitle: "Parcul Fotovoltaic Costinești",
      projectText: "Primul proiect Chespi Energy este dezvoltarea unui parc de panouri fotovoltaice la Costinești. Parcul se întinde pe 5 hectare și vizează o producție de 3MW pe an, începând din 2026.",
      stats: [
        { value: "5 ha", label: "Suprafața parcului" },
        { value: "3 MW", label: "Producție anuală" },
        { value: "2026", label: "Operațional din" },
      ],
      back: "Înapoi la pagina principală",
    },
    tourism: {
      overline: "Chespi Hospitality",
      heroBrand: "Diplomatic Event Center",
      heroTagline: "Acolo unde Marea Neagră întâlnește arta sărbătorii.",
      diploLead: "Liderul evenimentelor de lux din Mamaia.",
      diploText:
        "Cu vedere la plajele din Mamaia, Diplomatic găzduiește nunți, botezuri și evenimente corporate în saloane ce se împart după fiecare ocazie — cu două piscine și o terasă generoasă, la câțiva pași de mare.",
      diploBody: [
        "Finalizat în 2018, Diplomatic Event Center are vedere la plajele din Mamaia și la Marea Neagră — liderul pieței din Constanța, cu cel mai înalt nivel de lux, în servicii și în ținută.",
        "Arhitectura clasică a interiorului se îmbină cu un exterior modern și minimalist, creând o ambianță remarcabilă pentru orice ocazie. Cu o capacitate de 1.000+ de invitați, spațiul se împarte în două, trei sau patru saloane și se deschide către o terasă generoasă cu două piscine, la câțiva pași de mare.",
        "De la nunți, botezuri și pool party-uri până la seri corporate și conferințe — indiferent ce organizezi, echipa noastră este alături de tine de la cel mai mic detaliu până la momentul în sine.",
      ],
      diploCta: "complexdiplomatic.ro",
      lumiereLabel: "Salon Lumière",
      salonsLabel: "Salon Sunrise & Sunset",
      stats: [
        { value: "1000+", label: "Capacitate invitați" },
        { value: "4", label: "Saloane modulare" },
        { value: "2", label: "Piscine" },
        { value: "2.533 m²", label: "Suprafață construită" },
      ],
      oroBrand: "Oro Restaurant",
      oroLead: "Bucătărie europeană modernă, direct pe plajă.",
      oroText:
        "Un restaurant bijou în incinta complexului Diplomatic — marmură, accente aurii și doar 60 de locuri, astfel încât exclusivitatea se regăsește în fiecare farfurie.",
      oroBody: [
        "Deschis în 2019 și aflat în incinta complexului Diplomatic Event Center — în inima Mamaiei, direct pe plajă — Oro este una dintre cele mai rafinate mese ale stațiunii, servind bucătărie europeană modernă.",
        "Serile de live cooking aduc bucătăria pe terasă, unde cheferii gătesc în fața ta în timp ce soarele apune peste Marea Neagră.",
      ],
      enigmaBrand: "Enigma Resort",
      enigmaLead: "O evadare de patru stele, la câțiva metri de nisipul fin al Mamaiei.",
      enigmaText:
        "Șase bungalouri înconjurate de 2000 m² de verdeață, plajă privată și mic dejun la Oro.",
      enigmaCta: "Rezervă pe Booking.com",
      cinqueBrand: "Cinque Sensi",
      cinqueLead: "Pizza italiană autentică, în inima Constanței.",
      cinqueBody: [
        "La Cinque Sensi Pizzerie aducem gustul autentic al Italiei la Constanța — pizza preparată manual, cu ingrediente premium, aluat cu fermentare lentă și pasiune pentru calitate. Fiecare felie este o sărbătoare a tradiției, a gustului și a excelenței.",
        "Angajamentul nostru față de calitate, prospețime și satisfacția clienților ne-a transformat într-o destinație preferată a iubitorilor de pizza care caută adevărata excelență italiană.",
      ],
      back: "Înapoi la pagina principală",
    },
    residential: {
      overline: "Meliora Group",
      heroBrand: "Meliora",
      heroTagline: "Locuire de lux pe malul Mării Negre.",
      heroLine2: "Creăm spații care inspiră, pentru generațiile viitoare.",
      lead: "Dezvoltări rezidențiale de referință în cele mai căutate locații din Constanța.",
      body: [
        "Meliora Group dezvoltă proiecte rezidențiale de referință — clădiri ambițioase, la scară mare, definite de arhitectură modernă îndrăzneață, finisaje premium și o calitate fără compromisuri.",
        "De la malul lacului Tăbăcărie până la faleza din Mamaia, construim la scară mare — adrese de referință gândite să ofere rezidenților un nivel distinct de exclusivitate.",
      ],
      towerBrand: "Meliora Tower Mamaia",
      towerLead: "Un reper exclusivist pe faleza din Mamaia.",
      towerBody: [
        "Meliora Tower Mamaia este o dezvoltare exclusivistă pe malul mării, gândită să ofere o experiență rezidențială rafinată în una dintre cele mai dorite locații din zonă. Cu 16 etaje pe un teren premium de 4.000 m² chiar la malul mării, proiectul îmbină arhitectura contemporană, facilități premium și priveliști panoramice asupra litoralului.",
        "Proiectată cu grijă pentru a îmbina sofisticarea și confortul, Meliora oferă reședințe spațioase, spații exterioare amenajate cu gust și un stil de viață definit de intimitate, confort și lux discret.",
      ],
      amenitiesTitle: "Facilități",
      amenities: ["Parcare privată", "Piscină exterioară", "Spa", "Sală de fitness", "Concierge 24/7", "Plajă privată"],
      tabacarieiBrand: "Meliora Tăbăcărie Lake",
      tabacarieiLead: "Primul nostru reper rezidențial, lângă lacul și parcul Tăbăcărie.",
      tabacarieiBody: [
        "Meliora Tăbăcărie Lake a fost primul pas al Meliora Group în sectorul imobiliar rezidențial. Finalizat în 2008, se află la intrarea de sud în Mamaia — o dezvoltare elegantă, de tip boutique, cu priveliști panoramice neîngrădite asupra parcului și lacului, într-o zonă liniștită și exclusivistă.",
        "Locația oferă acces rapid atât către centrul orașului, cât și către Mamaia. Cu un regim de înălțime G+5 și o suprafață totală de 1.635 m², dezvoltarea găzduiește 10 apartamente.",
      ],
      tabacarieiStats: [
        { value: "2008", label: "Finalizat" },
        { value: "G+5", label: "Regim de înălțime" },
        { value: "1.635 m²", label: "Suprafață totală" },
        { value: "10", label: "Apartamente" },
      ],
      poporuluiBrand: "Meliora Poporului",
      poporuluiLead: "Construit la cele mai înalte standarde, în inima Constanței.",
      poporuluiBody: [
        "Meliora Poporului a fost finalizat în 2010. Situat în inima Constanței, a fost construit la cele mai înalte standarde, oferind spații generoase de locuit și parcare subterană privată tuturor rezidenților.",
        "Proprietatea se remarcă prin arhitectura modernă, care permite apartamentelor să ofere locatarilor un nivel distinct de exclusivitate.",
      ],
      stats: [
        { value: "2010", label: "Finalizat" },
        { value: "G+5", label: "Regim de înălțime" },
        { value: "2.117 m²", label: "Suprafață totală" },
        { value: "10", label: "Apartamente" },
        { value: "2", label: "Spații comerciale" },
      ],
      back: "Înapoi la pagina principală",
    },
    taurus: {
      overline: "Imobiliare comerciale",
      brand: "Taurus Group",
      kicker: "Un sub-grup Chespi Group",
      heroLead:
        "Repere comerciale premiate, în cele mai importante locații din Constanța — gazdă pentru branduri recunoscute internațional.",
      established: "Din 1998 · Constanța",
      scroll: "Derulează",
      portfolioOverline: "Portofoliul",
      portfolioTitle: "Patru repere",
      portfolioLead:
        "Patru clădiri de birouri și retail care definesc amprenta comercială a Taurus Group în Constanța.",
      buildings: [
        { k: "diplomatic", index: "01", name: "Diplomatic Business Center", year: "2017", location: "Abator, Constanța", regime: "G+1", area: "3.332 m²", tag: "Cea mai mare proprietate comercială a grupului — gazdă pentru Jysk, Carrefour și Kik.", href: "#diplomatic" },
        { k: "ferdinand", index: "02", name: "46 Ferdinand Business Center", year: "2008", location: "Str. Ferdinand, Constanța", regime: "UG+G+3", area: "1.387 m²", tag: "Premiul pentru cea mai bună arhitectură a anului — gazdă pentru Laser Vision Med.", href: "#ferdinand" },
        { k: "mamaia", index: "03", name: "220 Mamaia Business Center", year: "1998", location: "Bd. Mamaia, Constanța", regime: "G+1", area: "700 m²", tag: "Prima clădire comercială a grupului, astăzi găzduiește Medstar 2000 și Toldimed.", href: "#mamaia" },
        { k: "dacia", index: "04", name: "Dacia Business Center", year: "2007", location: "Lăpușneanu × Tomis, Constanța", regime: "G+1", area: "160 m²", tag: "Achiziționată și complet renovată în 2007, la una dintre cele mai aglomerate intersecții ale orașului.", href: "#dacia" },
      ],
      diplomatic: {
        tag: "Reper principal",
        name: "Diplomatic Business Center",
        lead: "Cea mai mare proprietate comercială a Taurus Group în prezent.",
        body: [
          "Inițial centru de evenimente, Diplomatic Business Center a renăscut ca reper comercial modern în zona Abator din Constanța.",
          "Astăzi găzduiește o serie de magazine de retail de succes internațional — Jysk, Carrefour și Kik. Cu un regim G+1, 3.332 m² de suprafață închiriabilă și 25 de locuri de parcare, designul modern al clădirii completează frumos zona urbană în care se află.",
          "Dezvoltarea a fost gândită ca un upgrade pentru întregul cartier, aducând serviciile și produsele unor branduri recunoscute internațional mai aproape de comunitate.",
        ],
        specs: [
          { k: "Finalizat", v: "2017" },
          { k: "Regim", v: "G+1" },
          { k: "Suprafață închiriabilă", v: "3.332 m²" },
          { k: "Parcare", v: "25 locuri" },
          { k: "Locație", v: "Abator, Constanța" },
        ],
        tenantsLabel: "Găzduiește",
        tenants: ["Jysk", "Carrefour", "Kik"],
        galleryOverline: "Galerie",
        galleryTitle: "În interiorul Diplomatic",
      },
      ferdinand: {
        tag: "Premiat",
        name: "46 Ferdinand Business Center",
        award: "Premiul pentru cea mai bună arhitectură a anului — Constanța, 2008",
        lead: "Unul dintre cele mai importante active ale Taurus Group.",
        body: [
          "Construit în 2008, 46 Ferdinand Business Center a primit premiul pentru cea mai bună arhitectură a anului în Constanța.",
          "Se află pe Strada Ferdinand — una dintre cele mai importante artere comerciale și financiare ale orașului — remarcându-se printr-un standard de calitate de nivel internațional și fiind construit pentru a se adapta atât pentru birouri, cât și pentru spații comerciale.",
          "Astăzi găzduiește Laser Vision Med, una dintre cele mai de succes clinici de oftalmologie din România.",
        ],
        specs: [
          { k: "Finalizat", v: "2008" },
          { k: "Regim", v: "UG+G+3" },
          { k: "Suprafață construită", v: "1.387 m²" },
          { k: "Locație", v: "Str. Ferdinand, Constanța" },
        ],
      },
      mamaia: {
        tag: "Prima",
        name: "220 Mamaia Business Center",
        lead: "Prima clădire comercială a Taurus Group.",
        body: [
          "Situată pe cel mai cunoscut bulevard al Constanței, 220 Mamaia Business Center a fost construită la cele mai înalte standarde și gândită să se adapteze nevoilor oricărui chiriaș.",
          "Finalizată în 1998, este locul de unde a început povestea comercială a Taurus Group — iar astăzi găzduiește partenerii Medstar 2000 și Toldimed, o clinică medicală și una stomatologică.",
        ],
        specs: [
          { k: "Finalizat", v: "1998" },
          { k: "Regim", v: "G+1" },
          { k: "Suprafață construită", v: "700 m²" },
          { k: "Locație", v: "Bd. Mamaia, Constanța" },
        ],
        tenantLabel: "Găzduiește",
        tenants: ["Medstar 2000", "Toldimed"],
      },
      dacia: {
        tag: "Reper urban",
        name: "Dacia Business Center",
        lead: "Achiziționată și complet renovată de Taurus Group în 2007.",
        body: [
          "Aflată la intersecția bulevardelor Alexandru Lăpușneanu și Tomis, Dacia Business Center garantează oricărui chiriaș un flux constant de trafic, într-una dintre cele mai populate zone ale Constanței.",
          "Ferestrele mari și arhitectura modernă aduc un suflu proaspăt zonei, cu acces direct de la bulevard și parcare generoasă.",
        ],
        specs: [
          { k: "Achiziționat", v: "2007" },
          { k: "Regim", v: "G+1" },
          { k: "Suprafață construită", v: "160 m²" },
          { k: "Locație", v: "Lăpușneanu × Tomis, Constanța" },
        ],
        tenantLabel: "Găzduiește",
        tenants: ["Famous Fast Food"],
      },
      contactOverline: "Imobiliare comerciale",
      contactTitle: "Construiește cu Taurus",
      contactText: "Pentru închirieri și solicitări comerciale din portofoliul Taurus Group.",
      contactEmail: "commercial@chespigroup.ro",
      back: "Înapoi la pagina principală",
    },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("en");
  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
